import { Injectable } from '@angular/core';
import {
  CardQuotaRequestDetail,
  CardQuotaRequestListRow,
  CardQuotaRequestTableDto,
  SaveCardQuotaRequestPayload,
  SaveCardQuotaRequestResponse,
} from '../../models/card-quota/card-quota-request.model';

@Injectable({ providedIn: 'root' })
export class MapperCardQuotaRequestService {
  mapTable(dto: CardQuotaRequestTableDto): {
    items: CardQuotaRequestListRow[];
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
  } {
    const raw = dto as CardQuotaRequestTableDto & Record<string, unknown>;
    const rows = (dto.data ?? raw['Data'] ?? []) as CardQuotaRequestListRow[];
    return {
      items: rows.map((r) => this.mapListRow(r as CardQuotaRequestListRow & Record<string, unknown>)),
      total: Number(dto.total ?? raw['Total'] ?? 0),
      page: Number(dto.page ?? raw['Page'] ?? 1),
      pageSize: Number(dto.pageSize ?? raw['PageSize'] ?? 20),
      totalPages: Number(dto.totalPages ?? raw['TotalPages'] ?? 0),
    };
  }

  mapListRow(row: CardQuotaRequestListRow & Record<string, unknown>): CardQuotaRequestListRow {
    return {
      id: Number(row.id ?? row['Id'] ?? 0),
      name: String(row.name ?? row['Name'] ?? ''),
      clientId: Number(row.clientId ?? row['ClientId'] ?? 0),
      clientName: String(row.clientName ?? row['ClientName'] ?? ''),
      siteId: Number(row.siteId ?? row['SiteId'] ?? 0),
      siteName: String(row.siteName ?? row['SiteName'] ?? ''),
      requestType: String(row.requestType ?? row['RequestType'] ?? ''),
      requestedQty: Number(row.requestedQty ?? row['RequestedQty'] ?? 0),
      approvedQty: row.approvedQty != null ? Number(row.approvedQty ?? row['ApprovedQty']) : null,
      status: String(row.status ?? row['Status'] ?? ''),
      cardScope: String(row.cardScope ?? row['CardScope'] ?? ''),
      quotaLevel: String(row.quotaLevel ?? row['QuotaLevel'] ?? ''),
      approvalRequestId:
        row.approvalRequestId != null ? Number(row.approvalRequestId ?? row['ApprovalRequestId']) : null,
      createDate: row.createDate != null ? String(row.createDate ?? row['CreateDate']) : null,
    };
  }

  mapDetail(dto: CardQuotaRequestDetail & Record<string, unknown>): CardQuotaRequestDetail {
    return {
      id: Number(dto.id ?? dto['Id'] ?? 0),
      name: String(dto.name ?? dto['Name'] ?? ''),
      clientId: Number(dto.clientId ?? dto['ClientId'] ?? 0),
      clientName: String(dto.clientName ?? dto['ClientName'] ?? ''),
      siteId: Number(dto.siteId ?? dto['SiteId'] ?? 0),
      siteName: String(dto.siteName ?? dto['SiteName'] ?? ''),
      requestedQty: Number(dto.requestedQty ?? dto['RequestedQty'] ?? 0),
      approvedQty: dto.approvedQty != null ? Number(dto.approvedQty ?? dto['ApprovedQty']) : null,
      requestType: String(dto.requestType ?? dto['RequestType'] ?? 'increase'),
      targetQuotaId: dto.targetQuotaId != null ? Number(dto.targetQuotaId ?? dto['TargetQuotaId']) : null,
      suspendCardMode: dto.suspendCardMode != null ? String(dto.suspendCardMode ?? dto['SuspendCardMode']) : null,
      effectiveDate: dto.effectiveDate != null ? String(dto.effectiveDate ?? dto['EffectiveDate']) : null,
      quotaLevel: String(dto.quotaLevel ?? dto['QuotaLevel'] ?? 'master'),
      cardScope: String(dto.cardScope ?? dto['CardScope'] ?? 'free'),
      cardTypeId: dto.cardTypeId != null ? Number(dto.cardTypeId ?? dto['CardTypeId']) : null,
      subscriptionId: dto.subscriptionId != null ? Number(dto.subscriptionId ?? dto['SubscriptionId']) : null,
      status: String(dto.status ?? dto['Status'] ?? 'draft'),
      approvalRequestId:
        dto.approvalRequestId != null ? Number(dto.approvalRequestId ?? dto['ApprovalRequestId']) : null,
      quotaId: dto.quotaId != null ? Number(dto.quotaId ?? dto['QuotaId']) : null,
      createDate: dto.createDate != null ? String(dto.createDate ?? dto['CreateDate']) : null,
    };
  }

  mapToPayload(
    form: Partial<CardQuotaRequestDetail> & { requestRemark?: string },
    userId: number,
    menuId: number,
    submitForApproval: boolean
  ): SaveCardQuotaRequestPayload {
    return {
      userId,
      menuId,
      menuListId: menuId,
      id: form.id && form.id > 0 ? form.id : null,
      clientId: Number(form.clientId ?? 0),
      siteId: Number(form.siteId ?? 0),
      requestedQty: Number(form.requestedQty ?? 0),
      requestType: form.requestType || 'increase',
      targetQuotaId: form.targetQuotaId ?? null,
      suspendCardMode: form.suspendCardMode ?? null,
      effectiveDate: form.effectiveDate || null,
      quotaLevel: form.quotaLevel || 'master',
      cardScope: form.cardScope || 'free',
      cardTypeId: form.cardTypeId ?? null,
      subscriptionId: form.subscriptionId ?? null,
      requestRemark: form.requestRemark ?? null,
      submitForApproval,
    };
  }

  mapSaveResponse(res: unknown): SaveCardQuotaRequestResponse {
    const raw = (res ?? {}) as Record<string, unknown>;
    return {
      id: Number(raw['id'] ?? raw['Id'] ?? 0),
      name: String(raw['name'] ?? raw['Name'] ?? ''),
      status: String(raw['status'] ?? raw['Status'] ?? ''),
      requiresApproval: Boolean(raw['requiresApproval'] ?? raw['RequiresApproval'] ?? false),
      approvalRequestId:
        raw['approvalRequestId'] != null || raw['ApprovalRequestId'] != null
          ? Number(raw['approvalRequestId'] ?? raw['ApprovalRequestId'])
          : null,
    };
  }

  createEmptyForm(siteId: number): CardQuotaRequestDetail {
    return {
      id: 0,
      name: '',
      clientId: 0,
      clientName: '',
      siteId,
      siteName: '',
      requestedQty: 0,
      approvedQty: null,
      requestType: 'increase',
      targetQuotaId: null,
      suspendCardMode: null,
      effectiveDate: null,
      quotaLevel: 'master',
      cardScope: 'free',
      cardTypeId: null,
      subscriptionId: null,
      status: 'draft',
      approvalRequestId: null,
      quotaId: null,
      createDate: null,
    };
  }

  requestTypeLabel(type: string): string {
    const map: Record<string, string> = {
      increase: 'ขอเพิ่ม',
      decrease: 'ขอลด',
      suspend: 'ระงับ',
      resume: 'ยกเลิกระงับ',
    };
    return map[type] ?? type;
  }

  statusLabel(status: string): string {
    const map: Record<string, string> = {
      draft: 'Draft',
      pending: 'รออนุมัติ',
      approved: 'อนุมัติแล้ว',
      rejected: 'ปฏิเสธ',
      cancelled: 'ยกเลิก',
    };
    return map[status] ?? status;
  }
}
